// Admin Page - Dashboard for managing waitlist and sessions
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, FileText, Mail, Calendar,
  ChevronDown, ChevronUp, ExternalLink, ArrowLeft,
  CheckCircle, Clock, DollarSign, Target, Lightbulb,
  Plus, Trash2, Save, RefreshCw, UserCog,
  Cog, UtensilsCrossed, Leaf, ShoppingCart, Heart, GraduationCap,
  Palette, Home as HomeIcon, Scale, Truck, Sun, Factory, Shield, Film,
  Trophy, Plane, PawPrint, HandHeart, CreditCard, AlertTriangle, Check, X
} from 'lucide-react';
import GoldenSpiral from '../components/GoldenSpiral';
import LicenseAdmin from '../components/LicenseAdmin';
import { useAuth } from '../contexts/AuthContext';
import { getAdminData, getExamples, saveExamples, addExample, deleteExample, manageAdmins, getStripePlans, createStripePlan, syncStripePlans } from '../services/api';

// Category options for dropdown
const CATEGORIES = [
  { value: 'hardware', label: 'Hardware & Robotics', icon: Cog },
  { value: 'finance', label: 'Finance & Business', icon: DollarSign },
  { value: 'food', label: 'Food & Restaurant', icon: UtensilsCrossed },
  { value: 'agriculture', label: 'Agriculture', icon: Leaf },
  { value: 'ecommerce', label: 'E-commerce & Retail', icon: ShoppingCart },
  { value: 'healthcare', label: 'Healthcare & Wellness', icon: Heart },
  { value: 'education', label: 'Education & Research', icon: GraduationCap },
  { value: 'creative', label: 'Creative & Design', icon: Palette },
  { value: 'realestate', label: 'Real Estate', icon: HomeIcon },
  { value: 'legal', label: 'Legal & Compliance', icon: Scale },
  { value: 'transportation', label: 'Transportation & Logistics', icon: Truck },
  { value: 'energy', label: 'Energy & Environment', icon: Sun },
  { value: 'manufacturing', label: 'Manufacturing & Industrial', icon: Factory },
  { value: 'security', label: 'Security & Safety', icon: Shield },
  { value: 'entertainment', label: 'Entertainment & Media', icon: Film },
  { value: 'sports', label: 'Sports & Fitness', icon: Trophy },
  { value: 'travel', label: 'Travel & Hospitality', icon: Plane },
  { value: 'home', label: 'Home & DIY', icon: HomeIcon },
  { value: 'pets', label: 'Pets & Animals', icon: PawPrint },
  { value: 'nonprofit', label: 'Non-Profit & Social Good', icon: HandHeart },
  { value: 'default', label: 'Other', icon: Lightbulb },
];

// Default admin emails (fallback for initial check before data loads)
const DEFAULT_ADMIN_EMAILS = ['ido@jacob-ai.com', 'greenvh@gmail.com'];

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('waitlist');
  const [waitlist, setWaitlist] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);

  // Examples management state
  const [newExampleText, setNewExampleText] = useState('');
  const [newExampleCategory, setNewExampleCategory] = useState('default');
  const [savingExample, setSavingExample] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');

  // User filter state
  const [filterUser, setFilterUser] = useState('all');
  // Step filter state
  const [filterStep, setFilterStep] = useState('all');

  // Admin management state
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [savingAdmin, setSavingAdmin] = useState(false);

  // Pricing management state
  const [stripePlans, setStripePlans] = useState([]);
  const [stripeMode, setStripeMode] = useState('test');
  const [unmappedProducts, setUnmappedProducts] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [newPlan, setNewPlan] = useState({
    name: '',
    price: '',
    callsIncluded: '',
    overageRate: '',
    overageAllowed: true
  });
  const [creatingPlan, setCreatingPlan] = useState(false);

  // Check if user is admin - use role from backend, or fall back to email list
  const adminEmails = admins.length > 0 ? admins : DEFAULT_ADMIN_EMAILS;
  const isAdmin = user && (user.role === 'admin' || adminEmails.includes(user.email));

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!isAdmin) {
      navigate('/home');
      return;
    }

    loadData();
  }, [user, isAdmin, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [adminData, examplesData] = await Promise.all([
        getAdminData(),
        getExamples()
      ]);
      setWaitlist(adminData.waitlist || []);
      setSessions(adminData.sessions || []);
      setUsers(adminData.users || []);
      setAdmins(adminData.admins || []);
      setExamples(examplesData.examples || []);
    } catch (err) {
      console.error('Failed to load admin data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExample = async () => {
    if (!newExampleText.trim()) return;

    setSavingExample(true);
    try {
      const result = await addExample({
        text: newExampleText.trim(),
        category: newExampleCategory
      });
      if (result.success) {
        setExamples([...examples, result.example]);
        setNewExampleText('');
        setNewExampleCategory('default');
      }
    } catch (err) {
      setError('Failed to add example: ' + err.message);
    } finally {
      setSavingExample(false);
    }
  };

  const handleDeleteExample = async (exampleId) => {
    if (!confirm('Are you sure you want to delete this example?')) return;

    try {
      await deleteExample(exampleId);
      setExamples(examples.filter(e => e.id !== exampleId));
    } catch (err) {
      setError('Failed to delete example: ' + err.message);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminEmail.trim()) return;

    setSavingAdmin(true);
    setError(null);
    try {
      const result = await manageAdmins('add', newAdminEmail.trim().toLowerCase());
      if (result.success) {
        setAdmins(result.admins);
        setNewAdminEmail('');
      }
    } catch (err) {
      setError('Failed to add admin: ' + err.message);
    } finally {
      setSavingAdmin(false);
    }
  };

  const handleRemoveAdmin = async (email) => {
    if (email === user.email) {
      setError('Cannot remove yourself as admin');
      return;
    }
    if (!confirm(`Are you sure you want to remove ${email} as admin?`)) return;

    setError(null);
    try {
      const result = await manageAdmins('remove', email);
      if (result.success) {
        setAdmins(result.admins);
      }
    } catch (err) {
      setError('Failed to remove admin: ' + err.message);
    }
  };

  // Load Stripe plans
  const loadStripePlans = async () => {
    setPricingLoading(true);
    try {
      const data = await getStripePlans();
      setStripePlans(data.plans || []);
      setStripeMode(data.stripeMode || 'test');
      setUnmappedProducts(data.unmappedProducts || []);
    } catch (err) {
      setError('Failed to load pricing plans: ' + err.message);
    } finally {
      setPricingLoading(false);
    }
  };

  // Create new plan in Stripe
  const handleCreatePlan = async () => {
    if (!newPlan.name || !newPlan.price) {
      setError('Name and price are required');
      return;
    }

    setCreatingPlan(true);
    setError(null);
    try {
      const result = await createStripePlan({
        name: newPlan.name,
        price: parseFloat(newPlan.price),
        callsIncluded: parseInt(newPlan.callsIncluded) || 0,
        overageRate: parseFloat(newPlan.overageRate) || 0,
        overageAllowed: newPlan.overageAllowed
      });

      if (result.success) {
        // Show the config snippet
        alert(`Plan created in Stripe!\n\nAdd this to stripe-config.js:\n\n${newPlan.name.toLowerCase()}: {\n  productId: '${result.product.id}',\n  priceId: '${result.price.id}',\n  name: '${newPlan.name}',\n  price: ${result.configSnippet.price},\n  callsIncluded: ${result.configSnippet.callsIncluded},\n  overageRate: ${result.configSnippet.overageRate},\n  overageAllowed: ${result.configSnippet.overageAllowed}\n}`);
        setShowCreatePlan(false);
        setNewPlan({ name: '', price: '', callsIncluded: '', overageRate: '', overageAllowed: true });
        loadStripePlans();
      }
    } catch (err) {
      setError('Failed to create plan: ' + err.message);
    } finally {
      setCreatingPlan(false);
    }
  };

  // Sync with Stripe
  const handleSyncStripe = async () => {
    setPricingLoading(true);
    try {
      const result = await syncStripePlans();
      console.log('Stripe sync result:', result);
      // Reload the plans list
      await loadStripePlans();
    } catch (err) {
      setError('Failed to sync with Stripe: ' + err.message);
    } finally {
      setPricingLoading(false);
    }
  };

  // Get filtered examples
  const filteredExamples = filterCategory === 'all'
    ? examples
    : examples.filter(e => e.category === filterCategory);

  // Get category stats
  const categoryStats = examples.reduce((acc, e) => {
    const cat = e.category || 'default';
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});

  // Helper to get display name for a user
  const getUserDisplayLabel = (u) => {
    if (u.displayName && u.email) return `${u.displayName} (${u.email})`;
    if (u.displayName) return u.displayName;
    if (u.email) return u.email;
    if (u.id) return u.id.slice(0, 8) + '...';
    return 'Unknown';
  };

  // Get filtered sessions by user and step
  const filteredSessions = sessions.filter(s => {
    // Filter by user
    if (filterUser !== 'all' && s.userId !== filterUser && s.userEmail !== filterUser) {
      return false;
    }
    // Filter by step
    if (filterStep !== 'all') {
      const sessionStep = s.currentStep || (s.recommendedStack ? 'completed' : 'interview');
      if (filterStep !== sessionStep) {
        return false;
      }
    }
    return true;
  });

  // Helper to get step display info
  const getStepInfo = (session) => {
    const step = session.currentStep || (session.recommendedStack ? 'completed' : 'unknown');
    const progress = session.interviewProgress || {};
    const answeredCount = Object.keys(progress).length;

    switch (step) {
      case 'completed':
        return { label: 'Completed', color: 'green', icon: CheckCircle };
      case 'interview':
        return {
          label: answeredCount > 0 ? `Interview (${answeredCount} answers)` : 'Interview',
          color: 'yellow',
          icon: Clock
        };
      default:
        return { label: 'Unknown', color: 'gray', icon: Clock };
    }
  };

  // Count sessions by step for filter dropdown
  const stepCounts = {
    all: sessions.length,
    interview: sessions.filter(s => (s.currentStep || (s.recommendedStack ? 'completed' : 'interview')) === 'interview').length,
    completed: sessions.filter(s => (s.currentStep || (s.recommendedStack ? 'completed' : 'interview')) === 'completed').length
  };

  // Helper to get session user display
  const getSessionUserDisplay = (session) => {
    if (session.userDisplayName && session.userEmail) {
      return `${session.userDisplayName} (${session.userEmail})`;
    }
    if (session.userDisplayName) return session.userDisplayName;
    if (session.userEmail) return session.userEmail;
    if (session.linkedEmail) return session.linkedEmail;
    if (session.userId) return session.userId.slice(0, 8) + '...';
    if (session.anonymousId) return `Anonymous (${session.anonymousId.slice(0, 8)}...)`;
    return 'Anonymous';
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">Access denied</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to App
          </button>

          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded flex items-center justify-center">
              <GoldenSpiral className="w-4 h-4" color="white" />
            </div>
            <span className="font-medium">VinciKit Admin</span>
          </div>

          <span className="text-sm text-gray-500">{user?.email}</span>
        </div>
      </header>

      <main className="px-6 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{waitlist.length}</p>
                  <p className="text-sm text-gray-500">Waitlist Signups</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{sessions.length}</p>
                  <p className="text-sm text-gray-500">Sessions Created</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {sessions.filter(s => s.recommendedStack).length}
                  </p>
                  <p className="text-sm text-gray-500">Stacks Generated</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {examples.length}
                  </p>
                  <p className="text-sm text-gray-500">Example Ideas</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setActiveTab('waitlist')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'waitlist'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Waitlist ({waitlist.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('sessions')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'sessions'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Sessions ({sessions.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('examples')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'examples'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                Examples ({examples.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('admins')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'admins'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <UserCog className="w-4 h-4" />
                Admins ({admins.length})
              </div>
            </button>
            <button
              onClick={() => { setActiveTab('pricing'); loadStripePlans(); }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'pricing'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Pricing
              </div>
            </button>
            <button
              onClick={() => setActiveTab('licenses')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'licenses'
                  ? 'bg-amber-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Licenses
              </div>
            </button>
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Waitlist Tab */}
          {activeTab === 'waitlist' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Goal
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stack
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Signed Up
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {waitlist.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                        No waitlist signups yet
                      </td>
                    </tr>
                  ) : (
                    waitlist.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-900">{entry.email}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600 max-w-xs truncate">
                          {entry.stack?.goal || 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          {entry.stack ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                              <CheckCircle className="w-3 h-3" />
                              {entry.stack.stack?.tools?.length || 0} tools
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-gray-500 text-sm">
                          {formatDate(entry.createdAt)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </motion.div>
          )}

          {/* Sessions Tab */}
          {activeTab === 'sessions' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              {/* Filters */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <div className="flex flex-wrap items-center gap-4">
                  {/* User Filter */}
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">User:</span>
                  </div>
                  <select
                    value={filterUser}
                    onChange={(e) => setFilterUser(e.target.value)}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-[250px]"
                  >
                    <option value="all">All Users ({sessions.length})</option>
                    {users.map(u => {
                      const userSessions = sessions.filter(s => s.userId === u.id || s.userEmail === u.email).length;
                      return (
                        <option key={u.id || u.email} value={u.id || u.email}>
                          {getUserDisplayLabel(u)} ({userSessions})
                        </option>
                      );
                    })}
                  </select>

                  {/* Step Filter */}
                  <div className="flex items-center gap-2 ml-4">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Status:</span>
                  </div>
                  <select
                    value={filterStep}
                    onChange={(e) => setFilterStep(e.target.value)}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-[180px]"
                  >
                    <option value="all">All Status ({stepCounts.all})</option>
                    <option value="interview">🟡 In Progress ({stepCounts.interview})</option>
                    <option value="completed">🟢 Completed ({stepCounts.completed})</option>
                  </select>

                  {(filterUser !== 'all' || filterStep !== 'all') && (
                    <button
                      onClick={() => { setFilterUser('all'); setFilterStep('all'); }}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      Clear filters
                    </button>
                  )}
                  <span className="ml-auto text-sm text-gray-500">
                    Showing {filteredSessions.length} of {sessions.length} sessions
                  </span>
                </div>
              </div>

              {filteredSessions.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-gray-500">
                  No sessions yet
                </div>
              ) : (
                filteredSessions.map((session) => (
                  <div
                    key={session.id}
                    className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedSession(
                        expandedSession === session.id ? null : session.id
                      )}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Target className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-gray-900 line-clamp-1">
                            {session.goal || 'No goal'}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <span className="text-blue-600 font-medium">
                              {getSessionUserDisplay(session)}
                            </span>
                            <span>•</span>
                            <span>{session.expertise || 'Unknown'}</span>
                            <span>•</span>
                            <span>{formatDate(session.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {(() => {
                          const stepInfo = getStepInfo(session);
                          const StepIcon = stepInfo.icon;
                          const colorClasses = {
                            green: 'bg-green-100 text-green-700',
                            yellow: 'bg-yellow-100 text-yellow-700',
                            gray: 'bg-gray-100 text-gray-600'
                          };
                          return (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${colorClasses[stepInfo.color]}`}>
                              <StepIcon className="w-3 h-3" />
                              {stepInfo.label}
                            </span>
                          );
                        })()}
                        {expandedSession === session.id ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </button>

                    {expandedSession === session.id && (
                      <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
                        <div className="grid grid-cols-2 gap-6">
                          {/* Interview Answers */}
                          <div>
                            <h4 className="font-medium text-gray-900 mb-3">Interview Answers</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-500">User:</span>
                                <span className="text-gray-900 font-medium">
                                  {getSessionUserDisplay(session)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Expertise:</span>
                                <span className="text-gray-900">{session.expertise || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Current Tools:</span>
                                <span className="text-gray-900">
                                  {Array.isArray(session.currentTools)
                                    ? session.currentTools.join(', ')
                                    : session.currentTools || 'N/A'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Budget:</span>
                                <span className="text-gray-900">{session.budget || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Timeline:</span>
                                <span className="text-gray-900">{session.timeline || 'N/A'}</span>
                              </div>
                              {session.additionalContext && (
                                <div className="pt-2 border-t border-gray-200">
                                  <span className="text-gray-500">Additional:</span>
                                  <p className="text-gray-900 mt-1">{session.additionalContext}</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Recommended Stack */}
                          <div>
                            <h4 className="font-medium text-gray-900 mb-3">Recommended Stack</h4>
                            {session.recommendedStack ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                                  <span className="text-blue-700 font-medium">AI:</span>
                                  <span className="text-blue-900">
                                    {session.recommendedStack.stack?.ai?.name || 'Claude'}
                                  </span>
                                </div>
                                <div className="space-y-1">
                                  <span className="text-sm text-gray-500">Tools:</span>
                                  {session.recommendedStack.stack?.tools?.map((tool, i) => (
                                    <div key={i} className="flex items-center gap-2 text-sm">
                                      <span className="w-2 h-2 bg-green-500 rounded-full" />
                                      <span className="text-gray-900">{tool.name}</span>
                                      <span className="text-gray-400">({tool.cost || 'Free'})</span>
                                    </div>
                                  )) || <span className="text-gray-400">No tools</span>}
                                </div>
                                <div className="pt-2 border-t border-gray-200 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Total Cost:</span>
                                    <span className="text-gray-900 font-medium">
                                      {session.recommendedStack.totalCost || 'N/A'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Deploy Time:</span>
                                    <span className="text-gray-900">
                                      {session.recommendedStack.deployTime || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <p className="text-gray-400">No stack generated yet</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </motion.div>
          )}

          {/* Examples Tab */}
          {activeTab === 'examples' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {/* Add New Example */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add New Example
                </h3>
                <div className="flex gap-4">
                  <input
                    type="text"
                    value={newExampleText}
                    onChange={(e) => setNewExampleText(e.target.value)}
                    placeholder="Enter example text (e.g., 'Build a smart home controller')"
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={newExampleCategory}
                    onChange={(e) => setNewExampleCategory(e.target.value)}
                    className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleAddExample}
                    disabled={!newExampleText.trim() || savingExample}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {savingExample ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    Add
                  </button>
                </div>
              </div>

              {/* Category Stats */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-medium text-gray-900 mb-4">Category Distribution</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setFilterCategory('all')}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      filterCategory === 'all'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    All ({examples.length})
                  </button>
                  {CATEGORIES.filter(cat => categoryStats[cat.value]).map(cat => {
                    const IconComponent = cat.icon;
                    return (
                      <button
                        key={cat.value}
                        onClick={() => setFilterCategory(cat.value)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-colors flex items-center gap-1.5 ${
                          filterCategory === cat.value
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <IconComponent className="w-3.5 h-3.5" />
                        {cat.label} ({categoryStats[cat.value] || 0})
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Examples List */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-medium text-gray-900">
                    {filterCategory === 'all' ? 'All Examples' : CATEGORIES.find(c => c.value === filterCategory)?.label}
                    <span className="text-gray-400 ml-2">({filteredExamples.length})</span>
                  </h3>
                  <button
                    onClick={loadData}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Refresh"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>

                <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
                  {filteredExamples.length === 0 ? (
                    <div className="px-6 py-12 text-center text-gray-500">
                      No examples found
                    </div>
                  ) : (
                    filteredExamples.map((example) => {
                      const cat = CATEGORIES.find(c => c.value === example.category) || CATEGORIES[CATEGORIES.length - 1];
                      const IconComponent = cat.icon;
                      return (
                        <div
                          key={example.id}
                          className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 group"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <IconComponent className="w-4 h-4 text-gray-500" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-gray-900 truncate">{example.text}</p>
                              <p className="text-xs text-gray-400">{cat.label}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteExample(example.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            title="Delete example"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Admins Tab */}
          {activeTab === 'admins' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {/* Add New Admin */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add New Admin
                </h3>
                <div className="flex gap-4">
                  <input
                    type="email"
                    value={newAdminEmail}
                    onChange={(e) => setNewAdminEmail(e.target.value)}
                    placeholder="Enter email address"
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddAdmin()}
                  />
                  <button
                    onClick={handleAddAdmin}
                    disabled={!newAdminEmail.trim() || savingAdmin}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {savingAdmin ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    Add Admin
                  </button>
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Admin users can access this dashboard and manage all settings.
                </p>
              </div>

              {/* Current Admins List */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-medium text-gray-900 flex items-center gap-2">
                    <UserCog className="w-5 h-5" />
                    Current Admins
                    <span className="text-gray-400 ml-2">({admins.length})</span>
                  </h3>
                  <button
                    onClick={loadData}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Refresh"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>

                <div className="divide-y divide-gray-100">
                  {admins.length === 0 ? (
                    <div className="px-6 py-12 text-center text-gray-500">
                      No admins found
                    </div>
                  ) : (
                    admins.map((email) => (
                      <div
                        key={email}
                        className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-600 font-medium text-sm">
                              {email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-gray-900 font-medium">{email}</p>
                            {email === user.email && (
                              <span className="text-xs text-green-600">You</span>
                            )}
                          </div>
                        </div>
                        {email !== user.email && (
                          <button
                            onClick={() => handleRemoveAdmin(email)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            title="Remove admin"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {/* Stripe Mode Indicator */}
              <div className={`px-4 py-3 rounded-lg flex items-center gap-3 ${
                stripeMode === 'live' ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <div className={`w-3 h-3 rounded-full ${stripeMode === 'live' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                <span className={`font-medium ${stripeMode === 'live' ? 'text-green-700' : 'text-yellow-700'}`}>
                  Stripe {stripeMode === 'live' ? 'Live' : 'Test'} Mode
                </span>
                {stripeMode === 'test' && (
                  <span className="text-yellow-600 text-sm">Changes will only affect test environment</span>
                )}
              </div>

              {/* Actions */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-900 flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Stripe Pricing Plans
                  </h3>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleSyncStripe}
                      disabled={pricingLoading}
                      className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center gap-2"
                    >
                      <RefreshCw className={`w-4 h-4 ${pricingLoading ? 'animate-spin' : ''}`} />
                      Sync with Stripe
                    </button>
                    <button
                      onClick={() => setShowCreatePlan(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Create Plan
                    </button>
                  </div>
                </div>
              </div>

              {/* Create Plan Form */}
              {showCreatePlan && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Create New Plan in Stripe
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name *</label>
                      <input
                        type="text"
                        value={newPlan.name}
                        onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                        placeholder="e.g., Pro Plus"
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price ($/month) *</label>
                      <input
                        type="number"
                        value={newPlan.price}
                        onChange={(e) => setNewPlan({ ...newPlan, price: e.target.value })}
                        placeholder="e.g., 99"
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">API Calls Included</label>
                      <input
                        type="number"
                        value={newPlan.callsIncluded}
                        onChange={(e) => setNewPlan({ ...newPlan, callsIncluded: e.target.value })}
                        placeholder="e.g., 1000"
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Overage Rate ($/call)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={newPlan.overageRate}
                        onChange={(e) => setNewPlan({ ...newPlan, overageRate: e.target.value })}
                        placeholder="e.g., 0.05"
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="overageAllowed"
                        checked={newPlan.overageAllowed}
                        onChange={(e) => setNewPlan({ ...newPlan, overageAllowed: e.target.checked })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="overageAllowed" className="text-sm text-gray-700">
                        Allow overage (charge per additional call beyond limit)
                      </label>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-4">
                    <button
                      onClick={handleCreatePlan}
                      disabled={creatingPlan || !newPlan.name || !newPlan.price}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {creatingPlan ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Create in Stripe
                    </button>
                    <button
                      onClick={() => setShowCreatePlan(false)}
                      className="px-6 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                  <p className="mt-3 text-sm text-gray-500">
                    Note: After creating, you'll need to add the product/price IDs to <code className="bg-gray-100 px-1 rounded">stripe-config.js</code>
                  </p>
                </div>
              )}

              {/* Plans List */}
              {pricingLoading ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
                  <p className="text-gray-500">Loading pricing plans...</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100">
                    <h3 className="font-medium text-gray-900">
                      Current Plans ({stripePlans.length})
                    </h3>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {stripePlans.length === 0 ? (
                      <div className="px-6 py-12 text-center text-gray-500">
                        No plans found. Click "Sync with Stripe" to load plans.
                      </div>
                    ) : (
                      stripePlans.map((plan) => (
                        <div key={plan.key} className="px-6 py-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                plan.syncStatus === 'synced' ? 'bg-green-100' : 'bg-red-100'
                              }`}>
                                {plan.syncStatus === 'synced' ? (
                                  <Check className="w-6 h-6 text-green-600" />
                                ) : (
                                  <AlertTriangle className="w-6 h-6 text-red-600" />
                                )}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-gray-900">{plan.name}</h4>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    plan.syncStatus === 'synced'
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}>
                                    {plan.syncStatus === 'synced' ? 'Synced' : 'Missing in Stripe'}
                                  </span>
                                </div>
                                <p className="text-gray-500 text-sm mt-1">
                                  ${plan.priceInDollars}/month • {plan.callsIncluded.toLocaleString()} calls included
                                  {plan.overageAllowed && ` • $${plan.overageRateInDollars}/extra call`}
                                </p>
                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                  <span>Key: <code className="bg-gray-100 px-1 rounded">{plan.key}</code></span>
                                  <span>Product: <code className="bg-gray-100 px-1 rounded">{plan.productId}</code></span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              {plan.stripePrice && (
                                <div className="text-sm">
                                  <span className="text-gray-500">Stripe Price: </span>
                                  <span className="font-medium">${(plan.stripePrice.unitAmount / 100).toFixed(2)}</span>
                                </div>
                              )}
                              {plan.stripeProduct && (
                                <div className="text-xs text-gray-400 mt-1">
                                  {plan.stripeProduct.name}
                                </div>
                              )}
                            </div>
                          </div>
                          {plan.syncStatus !== 'synced' && (
                            <div className="mt-3 p-3 bg-red-50 rounded-lg text-sm text-red-700">
                              <AlertTriangle className="w-4 h-4 inline mr-2" />
                              This plan's product or price is missing in Stripe. Create it using the form above.
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Unmapped Products */}
              {unmappedProducts.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-orange-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-orange-200 bg-orange-50">
                    <h3 className="font-medium text-orange-800 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Unmapped Stripe Products ({unmappedProducts.length})
                    </h3>
                    <p className="text-sm text-orange-600 mt-1">
                      These products exist in Stripe but are not mapped in stripe-config.js
                    </p>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {unmappedProducts.map((product) => (
                      <div key={product.id} className="px-6 py-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{product.name}</p>
                          <p className="text-sm text-gray-500">{product.description || 'No description'}</p>
                          <p className="text-xs text-gray-400 mt-1">ID: {product.id}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          product.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {product.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Licenses Tab */}
          {activeTab === 'licenses' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <LicenseAdmin />
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
